/** @format */

import { Brand } from "@question-bank/shared";
import type { ResolveRequest } from "./coreTaxonomy";

/**
 * In-memory cache for Core Taxonomy API reads (QB2-290).
 *
 * A question stores only Core ids in its metadata (`coreMappings`,
 * `specification.coreLineageId`); every id must be resolved back to its title
 * and data from the Core API to be displayed or used. Those reads repeat
 * constantly, so this cache serves a repeat read without a second network call.
 *
 * Lifetime: the cache lives for the lifetime of the module (a page load). It
 * has no expiry — Core taxonomy changes rarely, so a stale entry is corrected
 * on the next reload. Call {@link clearCoreTaxonomyCache} to drop everything,
 * for example at the start of an import run that must read fresh.
 *
 * Keys are deliberate. Reference-data reads key on `brand + path`. The subtopic
 * resolve keys on brand, subject, qualification, specification version, and the
 * NORMALISED statement text — never on a sub-topic code alone, because codes
 * drift between specification revisions (QB2-290 AC 3).
 */

const cache = new Map<string, Promise<unknown>>();

/**
 * Normalise a single statement-text segment: trim, collapse internal
 * whitespace to one space, and case-fold. The Core API applies the same
 * normalisation when matching, so the cache key and the lookup agree on what
 * "the same statement" means.
 */
export const normaliseStatementSegment = (value: string): string => value.trim().replace(/\s+/g, " ").toLowerCase();

/**
 * Build the cache key for a subtopic resolve. The statement text (theme, topic,
 * subtopic) is normalised and joined with a unit separator so that two
 * different segmentings cannot collide. The specification version is part of
 * the key; a sub-topic code never is.
 */
export const resolveCacheKey = (
	brand: Brand,
	subject: string,
	qualification: string,
	request: ResolveRequest,
): string => {
	const statement = [request.theme, request.topic, request.subtopic].map(normaliseStatementSegment).join("\u001F");
	return ["resolve", brand, subject, qualification, request.version, statement].join("::");
};

/** Build the cache key for a brand-scoped reference-data read. */
export const referenceCacheKey = (brand: Brand, path: string): string => ["ref", brand, path].join("::");

/**
 * Return the cached value for `key`, or call `loader` and cache its promise.
 *
 * The promise is cached, not just the resolved value, so concurrent callers
 * for the same key share one in-flight request (request de-duplication). A
 * rejected promise is evicted, so a failed read — an outage or a `404` — is
 * never cached and the next call retries.
 */
export const withCache = <T>(key: string, loader: () => Promise<T>): Promise<T> => {
	const existing = cache.get(key);
	if (existing) {
		return existing as Promise<T>;
	}

	const pending = loader().catch((error: unknown) => {
		cache.delete(key);
		throw error;
	});
	cache.set(key, pending);
	return pending;
};

/** Drop every cached entry. Use at the start of a run that must read fresh. */
export const clearCoreTaxonomyCache = (): void => {
	cache.clear();
};
